import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CalendarOff, Trash2 } from 'lucide-react'

import { ApiError } from '@/api/client'
import { studentsApi } from '@/api/endpoints'
import type { StudentBreak } from '@/api/types'
import { Alert } from '@/components/ui/Alert'
import { Button, IconButton } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { TextField } from '@/components/ui/Field'
import { useToast } from '@/components/ui/Toast'
import { formatDate } from '@/lib/format'

interface Props {
  studentId: string
  breaks: StudentBreak[]
}

/**
 * Okresy przerwy ucznia — wakacje, choroba, dłuższy wyjazd.
 *
 * W czasie przerwy system odmawia rezerwacji zajęć dla tego ucznia, więc lista
 * pełni podwójną rolę: informuje i chroni przed pomyłką przy wyborze ucznia
 * z listy w formularzu lekcji.
 */
export function StudentBreaks({ studentId, breaks }: Props) {
  const queryClient = useQueryClient()
  const { notifySuccess, notifyError } = useToast()

  const [od, setOd] = useState('')
  const [do_, setDo] = useState('')
  const [powod, setPowod] = useState('')
  const [blad, setBlad] = useState<string | null>(null)

  const odswiez = () => queryClient.invalidateQueries({ queryKey: ['students', studentId] })

  const dodaj = useMutation({
    mutationFn: () =>
      studentsApi.addBreak(studentId, {
        starts_on: od,
        ends_on: do_,
        reason: powod.trim() || null,
      }),
    onSuccess: async () => {
      await odswiez()
      setOd('')
      setDo('')
      setPowod('')
      setBlad(null)
      notifySuccess('Przerwa została zapisana.')
    },
    onError: (caught: unknown) => {
      setBlad(caught instanceof ApiError ? caught.message : 'Nie udało się zapisać przerwy.')
    },
  })

  const usun = useMutation({
    mutationFn: (breakId: string) => studentsApi.deleteBreak(studentId, breakId),
    onSuccess: async () => {
      await odswiez()
      notifySuccess('Przerwa została usunięta.')
    },
    onError: (caught: unknown) => {
      notifyError(caught instanceof ApiError ? caught.message : 'Nie udało się usunąć przerwy.')
    },
  })

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setBlad(null)
    dodaj.mutate()
  }

  const canSubmit = od !== '' && do_ !== '' && do_ >= od

  return (
    <Card>
      <CardHeader title="Przerwy w nauce" />
      <div className="p-6">
        <p className="mb-4 text-sm text-on-surface-variant">
          W zaznaczonym okresie nie da się zarezerwować zajęć dla tego ucznia.
        </p>

        {blad && (
          <Alert tone="error" className="mb-4">
            {blad}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="mb-6 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Od"
              type="date"
              value={od}
              onChange={(event) => setOd(event.target.value)}
              required
            />
            <TextField
              label="Do"
              type="date"
              value={do_}
              min={od || undefined}
              onChange={(event) => setDo(event.target.value)}
              required
            />
          </div>
          <TextField
            label="Powód (opcjonalnie)"
            value={powod}
            onChange={(event) => setPowod(event.target.value)}
            placeholder="np. wakacje, choroba"
          />
          <div className="flex justify-end">
            <Button type="submit" isLoading={dodaj.isPending} disabled={!canSubmit}>
              Dodaj przerwę
            </Button>
          </div>
        </form>

        {breaks.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-on-surface-variant">
            <CalendarOff size={16} aria-hidden="true" />
            Brak zapisanych przerw.
          </p>
        ) : (
          <ul className="divide-y divide-outline-variant">
            {breaks.map((item) => (
              <li key={item.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="font-medium text-on-surface">
                    {formatDate(item.starts_on)} – {formatDate(item.ends_on)}
                  </p>
                  {item.reason && (
                    <p className="truncate text-xs text-on-surface-variant">{item.reason}</p>
                  )}
                </div>
                <IconButton
                  label={`Usuń przerwę ${formatDate(item.starts_on)}`}
                  icon={<Trash2 size={16} />}
                  onClick={() => usun.mutate(item.id)}
                  disabled={usun.isPending}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  )
}
